// Eén laadteller voor de hele app (Joris, 4 nov: "laden duurt best lang en je hebt het niet in
// de gaten — dit speelt vaker"). In plaats van elk scherm apart een spinner te laten tonen,
// telt deze module elk lopend verzoek naar onze backend of Microsoft Graph, via fetch én axios.
// De Laadbalk bovenaan de app leest de teller. Een verzoek telt tot de response-headers binnen
// zijn; een stream (assistent-chat) houdt de balk dus niet aan.
//
// Alleen VOORGROND telt: een verzoek dat start binnen een paar seconden na een klik, toets,
// paginawissel of het openen van de app — of terwijl er al een voorgrondverzoek loopt of net
// klaar is (een scherm dat in stappen laadt). Polls die vanzelf afgaan (meldingen, jobstatus,
// minuutrondes) tellen zo automatisch niet mee. Daarnaast is alles wat vanuit een
// setInterval-callback start achtergrond (polls lopen vrijwel altijd via setInterval; een
// debounce via setTimeout telt wel). Expliciet: `stil: true` (nooit tellen).

// Telemetrie, inloggen en statische bestanden tellen niet: daar wacht niemand op.
const NEGEER = [
  /login\.microsoftonline\.com/i,
  /login\.live\.com/i,
  /applicationinsights\.azure\.com/i,
  /dc\.services\.visualstudio\.com/i,
  /monitor\.azure\.com/i,
  /fonts\.(googleapis|gstatic)\.com/i,
  /\/static\//i,
  /\.(js|css|png|jpe?g|svg|woff2?|ico|json)(\?|$)/i,
];

let lopend = 0;
const luisteraars = new Set();

const VOORGROND_MS = 3000;   // zo lang na een actie geldt een nieuw verzoek als 'gevraagd'
const KETEN_MS = 1000;       // vervolgverzoek kort na een voorgrondverzoek hoort er nog bij
const KETEN_MAX_MS = 15000;  // maar een keten loopt nooit langer dan dit na de laatste actie
const MAX_TELDUUR_MS = 30000; // een hangend verzoek houdt de balk niet eindeloos vast
let laatsteActie = Date.now(); // het openen van de app telt als actie
let laatsteKlaar = 0;

let inInterval = 0;
/** Loopt de code nu (synchroon) binnen een setInterval-callback, dus een poll? */
export const binnenInterval = () => inInterval > 0;

/** Er gebeurde iets door de gebruiker (klik, toets, paginawissel). */
export function meldActie() { laatsteActie = Date.now(); }

/** Hoort een verzoek dat nu start bij iets waar de gebruiker op wacht? */
export function isVoorgrond(nu = Date.now()) {
  const sindsActie = nu - laatsteActie;
  if (sindsActie < VOORGROND_MS) return true;
  // Keten (scherm laadt in stappen), maar begrensd: anders houdt een trage poll die elkaar
  // overlapt zichzelf eindeloos 'voorgrond'.
  return sindsActie < KETEN_MAX_MS && (lopend > 0 || nu - laatsteKlaar < KETEN_MS);
}

const meld = () => luisteraars.forEach((f) => { try { f(lopend); } catch { /* luisteraar mag niet breken */ } });

/** Moet dit verzoek meetellen? Relatieve URL's alleen onder /Api. */
export function teltMee(url) {
  const u = String(url || '');
  if (!u) return false;
  if (NEGEER.some((re) => re.test(u))) return false;
  if (/^https?:\/\//i.test(u)) return true;
  return u.startsWith('/Api') || u.startsWith('/api');
}

export function begin() { lopend += 1; meld(); }
export function klaar() { lopend = Math.max(0, lopend - 1); laatsteKlaar = Date.now(); meld(); }

/** Begin met tellen; geeft een eenmalige afmelding terug (ook na MAX_TELDUUR_MS vanzelf). */
function telEenmalig() {
  begin();
  let af = false;
  const timer = setTimeout(() => meldAf(), MAX_TELDUUR_MS);
  function meldAf() {
    if (af) return;
    af = true;
    clearTimeout(timer);
    klaar();
  }
  return meldAf;
}
export const aantalLopend = () => lopend;

/** Abonneer op wijzigingen van het aantal lopende verzoeken; geeft een afmeldfunctie terug. */
export function luister(f) {
  luisteraars.add(f);
  return () => luisteraars.delete(f);
}

function urlVan(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return String(input || '');
}

/** Eén keer bij het starten van de app: window.fetch en axios laten meetellen. */
export function installeerLaadTeller({ venster = typeof window !== 'undefined' ? window : null, axios = null } = {}) {
  if (venster && venster.fetch && !venster.fetch.__laadTeller) {
    const echt = venster.fetch.bind(venster);
    const geteld = (input, init) => {
      if ((init && init.stil) || binnenInterval() || !teltMee(urlVan(input)) || !isVoorgrond()) return echt(input, init);
      const af = telEenmalig();
      try {
        return echt(input, init).then((res) => { af(); return res; }, (err) => { af(); throw err; });
      } catch (err) {
        af();
        throw err;
      }
    };
    geteld.__laadTeller = true;
    venster.fetch = geteld;
    // Wat telt als actie: klik/tik, toets en paginawissel (React Router gebruikt pushState).
    if (venster.addEventListener) {
      venster.addEventListener('pointerdown', meldActie, true);
      venster.addEventListener('keydown', meldActie, true);
      venster.addEventListener('popstate', meldActie);
    }
    if (venster.setInterval && !venster.setInterval.__laadTeller) {
      const echtInterval = venster.setInterval.bind(venster);
      const metMarkering = (fn, ...rest) => echtInterval(typeof fn !== 'function' ? fn : function poll(...a) {
        inInterval += 1;
        try { return fn.apply(this, a); } finally { inInterval -= 1; }
      }, ...rest);
      metMarkering.__laadTeller = true;
      venster.setInterval = metMarkering;
    }
    const h = venster.history;
    if (h && h.pushState && !h.pushState.__laadTeller) {
      const push = h.pushState.bind(h);
      h.pushState = (...a) => { meldActie(); return push(...a); };
      h.pushState.__laadTeller = true;
    }
  }
  if (axios && axios.interceptors && !axios.__laadTeller) {
    axios.__laadTeller = true;
    // Andere (asynchrone) interceptors schuiven onze interceptor door naar later; dan is een
    // interval-callback al voorbij. Daarom de poll-context vastleggen op het moment van de
    // aanroep zelf (axios.get/post/...), vóór de interceptorketen.
    const METHODES_ZONDER_DATA = ['get', 'delete', 'head', 'options'];
    const METHODES_MET_DATA = ['post', 'put', 'patch'];
    const markeer = (config) => (binnenInterval() ? { ...(config || {}), stil: true } : config);
    METHODES_ZONDER_DATA.forEach((m) => {
      if (typeof axios[m] !== 'function') return;
      const echtM = axios[m];
      axios[m] = (url, config) => echtM(url, markeer(config));
    });
    METHODES_MET_DATA.forEach((m) => {
      if (typeof axios[m] !== 'function') return;
      const echtM = axios[m];
      axios[m] = (url, data, config) => echtM(url, data, markeer(config));
    });
    if (typeof axios.request === 'function') {
      const echtReq = axios.request;
      axios.request = (config) => echtReq(markeer(config));
    }
    axios.interceptors.request.use((config) => {
      if (!config.stil && teltMee(config.url) && isVoorgrond()) config.__laadAf = telEenmalig();
      return config;
    });
    const af = (config) => { if (config && config.__laadAf) { config.__laadAf(); config.__laadAf = null; } };
    axios.interceptors.response.use(
      (res) => { af(res.config); return res; },
      (err) => { af(err && err.config); return Promise.reject(err); },
    );
  }
}
